import { z } from 'zod';

import {
  AttachmentItem,
  CreateUpdateTemplate,
  FilterFormFields,
  NewSubTask,
  NewTask,
  NewTemplateField,
  ProcessTemplate,
  ProcessWorkflow,
  ProcessWorkflowTask,
  ProcessWorkflowTaskCreateUpdate,
  SubTask,
  Task,
  TaskComment,
} from './types';

import { paginatedParamsSchema, PaginatedResponse, restAPI } from '../rest-api';

import { TaskStatus } from '~/features/process-management/configs';
import { stripUndefined } from '~/utils/helpers';
import { AtLeastOne } from '~/utils/type-helpers';

function buildFilterQuery(filters?: FilterFormFields | null): string {
  if (!filters) return '';
  const params = new URLSearchParams();

  // Status filters
  Object.entries(filters.status).forEach(([key, value]) => {
    if (value)
      params.append(
        'status',
        String(TaskStatus[key as keyof typeof TaskStatus]),
      );
  });

  // Priority filters
  const priorityMap = { CRITICAL: 3, HIGH: 2, MEDIUM: 1, LOW: 0 };
  Object.entries(filters.priority).forEach(([key, value]) => {
    if (value)
      params.append(
        'priority',
        String(priorityMap[key as keyof typeof priorityMap]),
      );
  });

  // Due date filters
  const { from, to } = filters.custom_date_value;
  if (filters.custom_date) {
    if (from)
      params.append('due_date_after', from.toISOString());
    if (to)
      params.append('due_date_before', to.toISOString());
  }

  if (filters.overdue) params.append('is_overdue', 'true');
  if (filters.no_due_date) params.append('has_no_due_date', 'true');

  // Assignee filters
  filters.assignee.forEach((id) => params.append('assignee', String(id)));
  if (filters.no_assignee) params.append('has_no_assignee', 'true');
  if (filters.is_assignee) params.append('is_assignee', 'true');

  return params.toString();
}

const endpoints = restAPI.injectEndpoints({
  endpoints: (builder) => ({
    // BACKOFFICE
    partnerProcessTemplates: builder.query<ProcessTemplate[], void>({
      query: () => ({ url: '/template' }),
      providesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'partner-process-templates' }] : [],
    }),
    createPartnerProcessTemplate: builder.mutation<ProcessTemplate, { payload: NewTemplateField, params: { is_partner_template: string } }>({
      query: ({ payload, params }) => ({
        url: '/template',
        method: 'POST',
        params: stripUndefined(params ?? {}),
        data: payload,
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'process-templates' }, { type: 'Tenant', id: 'partner-process-templates' }] : [],
    }),
    // SETTINGS
    processTemplates: builder.query<ProcessTemplate[], void>({
      query: () => ({ url: '/template/settings-list' }),
      providesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'process-templates' }] : [],
    }),
    processTemplate: builder.query<CreateUpdateTemplate, { id: string }>({
      query: (arg) => ({ url: `/template/${arg.id}` }),
      providesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'process-template' }] : [],
    }),
    createProcessTemplate: builder.mutation<ProcessTemplate, NewTemplateField>({
      query: (arg) => ({
        url: '/template',
        method: 'POST',
        data: arg,
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'process-templates' }] : [],
    }),
    updateProcessTemplate: builder.mutation<
      ProcessTemplate,
      Omit<ProcessTemplate, 'classification'>
    >({
      query: (arg) => ({
        url: `/template/${arg.id}`,
        method: 'PATCH',
        data: {
          name: arg.name,
        },
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'process-template' }] : [],
    }),
    deleteProcessTemplate: builder.mutation<void, { id: number }>({
      query: (arg) => ({
        url: `/template/${arg.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [{ type: 'Tenant', id: 'process-templates' }, { type: 'Tenant', id: 'partner-process-templates' }],
    }),
    // TEMPLATE TASKS
    createTask: builder.mutation<Task, NewTask>({
      query: (arg) => ({
        url: '/template-subtasks',
        method: 'POST',
        data: arg,
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'process-template' }] : [],
    }),
    createSubTask: builder.mutation<SubTask, NewSubTask>({
      query: (arg) => ({
        url: '/template-subtasks',
        method: 'POST',
        data: arg,
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'process-template' }] : [],
    }),
    updateTask: builder.mutation<SubTask, AtLeastOne<SubTask>>({
      query: ({ id, ...arg }) => ({
        url: `/template-subtasks/${id}`,
        method: 'PATCH',
        data: arg,
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'process-template' }] : [],
    }),
    deleteTask: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/template-subtasks/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'process-template' }] : [],
    }),
    //WORKFLOWS
    listReportWorkflows: builder.query<ProcessWorkflow[], { params: { reference_id: string, } }>({
      query: (args) => ({ url: '/workflow', params: stripUndefined(args.params ?? {}) }),
      providesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'report-process-template' }] : [],
    }),
    createReportWorkflow: builder.mutation<{ id: number }, { params: { reference_id: string }, payload: { name: string } }>({
      query: (args) => ({
        url: '/workflow',
        params: stripUndefined(args.params ?? {}),
        data: args.payload,
        method: 'POST',
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'report-process-template' }] : [],
    }),
    useProcessTemplate: builder.mutation<{ id: number }, { template_id: number, report_reference: string }>({
      query: ({ template_id, report_reference }) => ({
        url: `/template/${template_id}/workflow/${report_reference}`,
        method: 'POST',
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'report-process-template' }] : [],
    }),
    updateReportWorkflow: builder.mutation<void, { id: number, params: { reference_id: string }, payload: { name: string } }>({
      query: ({ id, params, payload }) => ({
        url: `/workflow/${id}`,
        params: stripUndefined(params ?? {}),
        data: payload,
        method: 'PATCH',
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'report-process-template' }, { type: 'Tenant', id: 'report-process-workflow-tasks' }] : [],
    }),
    deleteProcessWorkflow: builder.mutation<void, { workflow_id: number, params: { reference_id: string } }>({
      query: ({ workflow_id, params }) => ({
        url: `/workflow/${workflow_id}`,
        params: stripUndefined(params ?? {}),
        method: 'DELETE',
      }),
      invalidatesTags: () => [{ type: 'Tenant', id: 'report-process-template' }],
    }),
    // WORKFLOW TASKS
    listWorkflowtasks: builder.query<ProcessWorkflowTask[], { workflow_id: number, params?: FilterFormFields }>({
      query: ({ workflow_id, params }) => ({

        url: `/subtask/workflow/${workflow_id}?${buildFilterQuery(params)}`,
      }),
      providesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'report-process-workflow-tasks' }] : [],
    }),
    getWorkflowTask: builder.query<ProcessWorkflowTask, { subtask_id: number }>({
      query: ({ subtask_id }) => ({
        url: `/subtask/${subtask_id}`,
      }),
      providesTags: (res) =>
        res ? [{ type: 'Tenant', id: `report-process-workflow-tasks-${res.id}` }] : [],
    }),
    addWorkflowTask: builder.mutation<ProcessWorkflowTask, { payload: ProcessWorkflowTaskCreateUpdate }>({
      query: ({ payload }) => ({
        url: '/subtask',
        method: 'POST',
        data: stripUndefined(payload),
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'report-process-workflow-tasks' }, { type: 'Tenant', id: `report-process-workflow-tasks-${res.parent}` }] : [],
    }),
    updateWorkflowTask: builder.mutation<ProcessWorkflowTask, { subtask_id: number, workflow_id?: number, payload: AtLeastOne<ProcessWorkflowTaskCreateUpdate>, params?: FilterFormFields }>({
      query: ({ subtask_id, payload }) => ({
        url: `/subtask/${subtask_id}`,
        method: 'PATCH',
        data: stripUndefined(payload),
      }),
      onQueryStarted: async ({ subtask_id, workflow_id, payload, params }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          endpoints.util.updateQueryData('getWorkflowTask', { subtask_id }, (task) => {
            Object.assign(task, payload);
          }),
        );
        let patchListResult;
        if (workflow_id) {

          patchListResult = dispatch(
            endpoints.util.updateQueryData('listWorkflowtasks', { workflow_id, params }, (tasks) => {
              const taskIndex = tasks.findIndex((t) => t.id === subtask_id);
              if (taskIndex !== -1) {
                tasks[taskIndex] = {
                  ...tasks[taskIndex],
                  ...payload,  // Apply updates from the payload
                };
              }
            }),
          );
        }
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
          patchListResult?.undo();
        }
      },
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: `report-process-workflow-tasks-${res.parent}` }, { type: 'Tenant', id: `report-process-workflow-tasks-${res.id}` }, { type: 'Tenant', id: 'report-process-workflow-tasks' }] : [],
    }),
    deleteWorkflowTask: builder.mutation<void, { subtask_id: number }>({
      query: ({ subtask_id }) => ({
        url: `/subtask/${subtask_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [{ type: 'Tenant', id: 'report-process-workflow-tasks' }],
    }),

    // COMMENTS
    listAllComments: builder.query<PaginatedResponse<TaskComment>, { params: z.infer<typeof paginatedParamsSchema> & { subtask_id: number } }>({
      query: ({ params }) => ({
        url: '/comment',
        params: stripUndefined(params ?? {}),
      }),
      providesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'report-process-workflow-tasks-comments' }] : [],
    }),
    addTaskComment: builder.mutation<void, {
      payload: Pick<TaskComment, 'content' | 'file_ids' | 'report_files' | 'subtask'>
    }>({
      query: ({ payload }) => ({
        url: '/comment',
        method: 'POST',
        data: stripUndefined(payload),
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'report-process-workflow-tasks-comments' }] : [],
    }),
    updateTaskComment: builder.mutation<void, {
      comment_id: number,
      params: { subtask_id: number }
      payload: Partial<TaskComment>
    }>({
      query: ({ comment_id, params, payload }) => ({
        url: `/comment/${comment_id}`,
        method: 'PATCH',
        params: stripUndefined(params ?? {}),
        data: stripUndefined(payload),
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'report-process-workflow-tasks-comments' }] : [],
    }),
    deleteTaskComment: builder.mutation<void, { comment_id: number, params: { subtask_id: number } }>({
      query: ({ comment_id, params }) => ({
        url: `/comment/${comment_id}`,
        method: 'DELETE',
        params: stripUndefined(params ?? {}),
      }),
      invalidatesTags: () =>
        [{ type: 'Tenant', id: 'report-process-workflow-tasks-comments' }],
    }),

    // FILES
    listAllReportAttachments: builder.query<AttachmentItem[], { workflow_id: number, params: { reference_id: string } }>({
      query: ({ workflow_id, params }) => ({
        url: `/workflow/${workflow_id}/report-file-list`,
        params: stripUndefined(params ?? {}),
      }),
      providesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'report-process-workflow-attachments' }] : [],
    }),
  }),
  overrideExisting: false,
});

export const ProcessService = endpoints;
